import { createAsyncThunk } from '@reduxjs/toolkit';

import Message from 'view/shared/message';
import { RootState } from 'modules/store';
import { ErrorObject } from 'types/global';
import Errors from 'modules/shared/error/errors';
import selectors from 'modules/auth/authSelectors';
import { AccountType } from 'modules/auth/constants';
import applicationListSelectors from 'modules/application/list/applicationListSelectors';
import ApplicationService from 'modules/application/applicationService';
import { STATUS_APPROVED, STATUS_REJECTED } from 'view/paymentsPage/constants';
import { STATUS_FAILED } from 'view/payoutsPage/constants';

import {
  OptInError,
  PayoutDetails,
  RefundReasons,
  RefundsPayload,
  UsersPayoutDetails,
  UpdateRefundPayload,
  AuthorizablesPayload,
  InitiateRefundPayload,
  PaymentDetailsForRefunds,
  EmptyBeneficiaryNameError,
  ConnectToBankPayload,
  UserConnectPayoutsDetails,
  InitiatePayoutPayload,
  CancelPayoutPayload,
  DestinationsPayload,
  PayoutAuthorizationRules,
  PayoutAuthorizationRule,
  SchemaResponse,
  ValidatePayoutsPayload,
  PayoutsError,
  UploadSecretsResponse,
  UploadSecretsRequest,
} from './payout.types';
import PayoutService from './payouts.service';

export const getPayoutDetails = createAsyncThunk<
  PayoutDetails,
  void,
  {
    state: RootState;
    rejectValue: OptInError | ErrorObject;
  }
>('payouts/get-payout-details', async (p, { getState, rejectWithValue }) => {
  const { environment: appEnvironment, appToken } = getState().root.global;

  try {
    return await PayoutService.getPayoutDetailsForCurrentUser({
      appToken,
      appEnvironment,
    });
  } catch (error: unknown) {
    const errorObj: ErrorObject = error as ErrorObject;

    if (
      errorObj.response.status === 404 &&
      Errors.errorStatusOrStatusCode(errorObj) === 'UNKNOWN_APP_USER'
    ) {
      return rejectWithValue({ isOptInRequired: true });
    }

    Errors.handle(errorObj);
    return rejectWithValue(errorObj);
  }
});

export const getQueuedPayments = createAsyncThunk<
  AuthorizablesPayload,
  {
    page: number;
    pageSize: number;
    dateTo: string;
    dateFrom: string;
    maxAmount: number;
    minAmount: number;
    status: string;
    mechanism: string;
  },
  {
    state: RootState;
    rejectValue: OptInError | ErrorObject;
  }
>(
  'payouts/get-queued-payments',
  async (
    {
      page,
      pageSize,
      dateTo,
      dateFrom,
      maxAmount,
      minAmount,
      status,
      mechanism,
    },
    { getState, rejectWithValue },
  ) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    const body = {
      page_number: page,
      page_size: pageSize,
      ...(dateTo ? { to: dateTo } : {}),
      ...(dateFrom ? { from: dateFrom } : {}),
      ...(maxAmount ? { amount_max: maxAmount } : {}),
      ...(minAmount ? { amount_min: minAmount } : {}),
      ...(status ? { status_type: status } : {}),
    };

    try {
      return await PayoutService.getAuthorizables({
        body,
        appToken,
        appEnvironment,
        mechanism,
      });
    } catch (error: unknown) {
      const errorObj: ErrorObject = error as ErrorObject;

      if (
        errorObj.response.status === 404 &&
        Errors.errorStatusOrStatusCode(errorObj) === 'UNKNOWN_APP_USER'
      ) {
        return rejectWithValue({ isOptInRequired: true });
      }

      Errors.handle(errorObj);
      return rejectWithValue(errorObj);
    }
  },
);

export const getUsersPayoutDetails = createAsyncThunk<
  UsersPayoutDetails,
  void,
  {
    state: RootState;
    rejectValue: OptInError | ErrorObject;
  }
>(
  'payouts/get-users-payout-details',
  async (p, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      return await PayoutService.getPayoutDetailsForUsers({
        appToken,
        appEnvironment,
      });
    } catch (error: unknown) {
      const errorObj: ErrorObject = error as ErrorObject;

      if (
        errorObj.response.status === 404 &&
        Errors.errorStatusOrStatusCode(errorObj) === 'UNKNOWN_APP_USER'
      ) {
        return rejectWithValue({ isOptInRequired: true });
      }

      Errors.handle(errorObj);
      return rejectWithValue(errorObj);
    }
  },
);

export const optInForPayouts = createAsyncThunk<
  PayoutDetails,
  void,
  { state: RootState }
>(
  'payouts/opt-in-for-payouts',
  async (p, { dispatch, getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;
    const accountType = selectors.selectAccountType(getState());

    try {
      const response = await PayoutService.optInCurrentUserForPayouts({
        appToken,
        appEnvironment,
      });

      if (accountType === AccountType.retail) {
        dispatch(createSMEMockUsersForPayouts());
      }

      return response;
    } catch (error: unknown) {
      Errors.handle(error);
      return rejectWithValue(error);
    }
  },
);

export const connectBankForPayouts = createAsyncThunk<
  UserConnectPayoutsDetails,
  ConnectToBankPayload,
  { state: RootState }
>(
  'payouts/connect-for-payouts',
  async (body, { dispatch, getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      const response = await PayoutService.autoConnectUserForPayouts({
        body,
        appToken,
        appEnvironment,
      });
      if (response.status_type === 'FAILED') {
        Message.error('Failed to connect to Payouts');
      } else {
        dispatch(getPayoutDetails());
      }
      return response;
    } catch (error: unknown) {
      Errors.handle(error);
      return rejectWithValue(error);
    }
  },
);

export const createSMEMockUsersForPayouts = createAsyncThunk<
  void,
  void,
  { state: RootState }
>('payouts/create-sme-mock-users', async (p, { getState, rejectWithValue }) => {
  const appId = applicationListSelectors.selectApplicationId(getState());

  try {
    return await ApplicationService.postAddSMEMockUsers(appId);
  } catch (error: unknown) {
    Errors.handle(error);
    return rejectWithValue(error);
  }
});

export const initiatePayout = createAsyncThunk<
  void,
  { body: InitiatePayoutPayload; async: boolean },
  { state: RootState }
>(
  'payouts/initiate-payout',
  async ({ body, async }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      return await PayoutService.initiatePayout({
        body,
        async,
        appToken,
        appEnvironment,
      });
    } catch (error: unknown) {
      Message.error(Errors.selectMessage(error));
      return rejectWithValue({
        message: Errors.selectMessage(error),
        status: Errors.errorStatusOrStatusCode(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const cancelPayout = createAsyncThunk<
  void,
  CancelPayoutPayload,
  { state: RootState }
>(
  'payouts/cancel-payout',
  async (payload, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      return await PayoutService.cancelPayout({
        body: payload,
        appToken,
        appEnvironment,
      });
    } catch (error: unknown) {
      Message.error(Errors.selectMessage(error));
      return rejectWithValue({
        message: Errors.selectMessage(error),
        status: Errors.errorStatusOrStatusCode(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const validateBulkPayout = createAsyncThunk<
  ValidatePayoutsPayload,
  {
    body: File;
    accountId: string;
  },
  {
    state: RootState;
    rejectValue: { message: string } | PayoutsError | ErrorObject;
  }
>(
  'payouts/validate-bulk-payout',
  async ({ body, accountId }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      return await PayoutService.validateBulkPayout({
        body,
        accountId,
        appToken,
        appEnvironment,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        status: Errors.errorStatusOrStatusCode(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const getPayoutDestinations = createAsyncThunk<
  DestinationsPayload,
  {
    page: number;
    pageSize: number;
    query: string;
  },
  { state: RootState }
>(
  'payouts/get-destinations',
  async ({ page, pageSize, query }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    const body = {
      page_number: page,
      page_size: pageSize,
      ...(query ? { query } : {}),
    };

    try {
      return await PayoutService.getPayoutDestinations({
        body,
        appToken,
        appEnvironment,
      });
    } catch (error: unknown) {
      Errors.handle(error);
      return rejectWithValue(error);
    }
  },
);

export const createPayoutAuthorizationRule = createAsyncThunk<
  PayoutAuthorizationRules,
  PayoutAuthorizationRules,
  { state: RootState }
>(
  'payouts/create-authorization-rule',
  async (body, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      const response = await PayoutService.createPayoutAuthorizationRule({
        body,
        appToken,
        appEnvironment,
      });
      Message.success('Payout rule was successfully created');
      return response;
    } catch (error: unknown) {
      Errors.handle(error);
      return rejectWithValue(error);
    }
  },
);

export const updatePayoutAuthorizationRule = createAsyncThunk<
  PayoutAuthorizationRules,
  {
    ruleId: string;
    payload: PayoutAuthorizationRule;
  },
  { state: RootState }
>(
  'payouts/update-authorization-rule',
  async ({ ruleId, payload }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;
    try {
      const response = await PayoutService.updatePayoutAuthorizationRule({
        ruleId,
        payload,
        appToken,
        appEnvironment,
      });
      Message.success('Payout rule was successfully updated');
      return response;
    } catch (error: unknown) {
      Errors.handle(error);
      return rejectWithValue(error);
    }
  },
);

export const getPayoutAuthorizationRule = createAsyncThunk<
  PayoutAuthorizationRules,
  void,
  { state: RootState }
>(
  'payouts/get-authorization-rules',
  async (body, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      return await PayoutService.getPayoutAuthorizationRule({
        appToken,
        appEnvironment,
      });
    } catch (error: unknown) {
      Errors.handle(error);
      return rejectWithValue(error);
    }
  },
);

export const getRefunds = createAsyncThunk<
  RefundsPayload,
  { page: number; pageSize: number; status: string },
  {
    state: RootState;
  }
>(
  'payouts/get-refunds',
  async ({ status, page, pageSize }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      return await PayoutService.getRefunds({
        page,
        status,
        pageSize,
        appToken,
        appEnvironment,
      });
    } catch (error: unknown) {
      const errorObj: ErrorObject = error as ErrorObject;

      if (errorObj.response.status === 404) {
        return rejectWithValue(errorObj);
      }

      Errors.handle(errorObj);
      return rejectWithValue(errorObj);
    }
  },
);

export const getRefundReasons = createAsyncThunk<
  RefundReasons[],
  void,
  { state: RootState }
>('payouts/get-refund-reasons', async (p, { getState, rejectWithValue }) => {
  const { environment: appEnvironment, appToken } = getState().root.global;

  try {
    return await PayoutService.getRefundReasons({ appEnvironment, appToken });
  } catch (error: unknown) {
    Errors.handle(error);
    return rejectWithValue(error);
  }
});

export const getPaymentRefundDetailsById = createAsyncThunk<
  PaymentDetailsForRefunds,
  { paymentId: string },
  { state: RootState; rejectValue: EmptyBeneficiaryNameError | ErrorObject }
>(
  'payouts/get-payment-refund-details',
  async ({ paymentId }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      return await PayoutService.getPaymentRefundDetailsById({
        appToken,
        paymentId,
        appEnvironment,
      });
    } catch (error: unknown) {
      const errorObj: ErrorObject = error as ErrorObject;

      if (
        errorObj.response.status === 422 &&
        Errors.errorStatusOrStatusCode(errorObj) === 'UNKNOWN_BENEFICIARY'
      ) {
        return rejectWithValue({ isEmptyBeneficiaryNameError: true });
      }

      Errors.handle(errorObj);
      return rejectWithValue(errorObj);
    }
  },
);

export const postInitiateRefund = createAsyncThunk<
  unknown,
  InitiateRefundPayload[],
  { state: RootState }
>(
  'payouts/post-initiate-refund',
  async (body, { dispatch, getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      const response = await PayoutService.postInitiateRefund({
        body,
        appToken,
        appEnvironment,
      });

      dispatch(
        getRefunds({
          page: 0,
          status: '',
          pageSize: 25,
        }),
      );

      Message.success('Refund created successfully');
      return response;
    } catch (error: unknown) {
      const errorObj: ErrorObject = error as ErrorObject;

      if (errorObj.response.status === 400) {
        if (
          errorObj.data.message.includes(
            'more than refundable amount [0.000 AED]',
          )
        ) {
          Message.error('Payment already fully refunded');
        } else if (
          errorObj.data.message.includes('is more than refundable amount')
        ) {
          const errorMessage = errorObj.data.message.split(
            ', refund(s) amount total',
          )[1];

          Message.error(`Refund amount ${errorMessage}`);
        } else if (
          errorObj.data.message.includes('The provided IBAN is not valid')
        ) {
          Message.error('The provided IBAN is not valid');
        } else if (
          errorObj.data.message.includes('amount must be greater than')
        ) {
          Message.error(
            'The amount must be above the minimum amount for your bank',
          );
        }
      } else {
        Errors.handle(error);
      }

      return rejectWithValue(errorObj.data);
    }
  },
);

export const updateRefund = createAsyncThunk<
  unknown,
  UpdateRefundPayload[],
  { state: RootState }
>(
  'payouts/put-refunds',
  async (body, { dispatch, getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      const response = await PayoutService.updateRefund({
        body,
        appToken,
        appEnvironment,
      });

      dispatch(
        getRefunds({
          page: 0,
          status: '',
          pageSize: 25,
        }),
      );

      if (body[0].status === STATUS_APPROVED) {
        Message.success('Refund Approved');
      } else if (body[0].status === STATUS_REJECTED) {
        Message.success('Refund Rejected');
      }
      return response;
    } catch (error: unknown) {
      Errors.handle(error);
      return rejectWithValue(error);
    }
  },
);

export const fetchSecretSchema = createAsyncThunk<
  SchemaResponse,
  string,
  { state: RootState }
>(
  'payouts/fetch-secret-schema',
  async (bankId, { getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;

    try {
      return await PayoutService.fetchSecretSchema({
        bankId,
        appToken,
        appEnvironment,
      });
    } catch (error: unknown) {
      Errors.handle(error);
      return rejectWithValue(error);
    }
  },
);

export const uploadSecrets = createAsyncThunk<
  UploadSecretsResponse,
  UploadSecretsRequest,
  { state: RootState }
>('payouts/upload-secrets', async (body, { getState, rejectWithValue }) => {
  const { environment: appEnvironment, appToken } = getState().root.global;

  try {
    const response = await PayoutService.uploadSecrets({
      body,
      appToken,
      appEnvironment,
    });
    if (response.status === STATUS_FAILED) {
      Message.error(response.message);
    } else {
      Message.success(response.message);
    }

    return response;
  } catch (error: unknown) {
    Errors.handle(error);
    return rejectWithValue(error);
  }
});

export const connectToBankUsingSecrets = createAsyncThunk<
  void,
  ConnectToBankPayload,
  { state: RootState }
>(
  'payouts/connect-to-bank-using-secrets',
  async (body, { dispatch, getState, rejectWithValue }) => {
    const { environment: appEnvironment, appToken } = getState().root.global;
    try {
      const response = await PayoutService.connectToBankUsingSecrets({
        body,
        appToken,
        appEnvironment,
      });

      Message.success('Payouts are enabled');
      dispatch(getPayoutDetails());
      return response;
    } catch (error: unknown) {
      Errors.handle(error);
      return rejectWithValue(error);
    }
  },
);
